import React, { useEffect, useRef, useState } from "react";
import { Heading, Box, HStack, VStack, Button, Input, IconButton, Select, Text } from "@chakra-ui/react";
import { FiSend } from "react-icons/fi";
import "./callSimulation.css";
import { wfDetails } from "views/admin/initiateCall/dataset";

export default function CallSimulation() {
  const [messages, setMessages] = useState([
    { id: 1, role: "bot", text: "" },
  ]);
  const [pendingText, setPendingText] = useState("");
  const [selectedWorkflow, setSelectedWorkflow] = useState(wfDetails[0] || { id: "", name: "" });
  const [phoneNumber, setPhoneNumber] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [conversationStarted, setConversationStarted] = useState(false);
  const [isInitiating, setIsInitiating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateSessionId = () => `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const appendBotTexts = (texts = []) => {
    if (!Array.isArray(texts)) return;
    const botMsgs = texts.map((t) => ({
      id: Date.now() + Math.random(),
      role: "bot",
      text: t,
    }));
    setMessages((prev) => [...prev, ...botMsgs]);
  };

  async function postToTelephonyInterface(body) {
    const res = await fetch("https://zet.quarkgen.ai/telephony_interface", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  const initiateConversation = async () => {
    if (!phoneNumber || !selectedWorkflow?.id) return;

    try {
      setIsInitiating(true);
      const newSessionId = generateSessionId();
      const payload = {
        user_id: phoneNumber.trim(),
        session_id: newSessionId,
        channel: "outbound",
        workflow_id: selectedWorkflow.id,
        msg: "/initial_message",
      };
      const resp = await postToTelephonyInterface(payload);
      setSessionId(newSessionId);
      setConversationStarted(true);

      const botTexts = Array.isArray(resp?.data) ? resp.data.map((d) => d?.text).filter(Boolean) : [];
      appendBotTexts(botTexts);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), role: "bot", text: "Failed to initiate conversation. Please try again." },
      ]);
    } finally {
      setIsInitiating(false);
    }
  };

  const handleSend = async () => {
    const trimmed = pendingText.trim();
    if (!trimmed) return;

    if (!conversationStarted) {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), role: "bot", text: "Please initiate the conversation first." },
      ]);
      return;
    }

    const userMessage = { id: Date.now(), role: "user", text: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setPendingText("");

    try {
      setIsSending(true);
      const payload = {
        user_id: phoneNumber.trim(),
        session_id: sessionId,
        channel: "outbound",
        workflow_id: selectedWorkflow.id,
        msg: trimmed,
      };
      const resp = await postToTelephonyInterface(payload);
      const botTexts = Array.isArray(resp?.data) ? resp.data.map((d) => d?.text).filter(Boolean) : [];
      appendBotTexts(botTexts);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), role: "bot", text: "Failed to send message. Please try again." },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const onWorkflowChange = (wfId) => {
    const match = wfDetails.find((w) => w.id === wfId);
    if (match) setSelectedWorkflow(match);
  };

  const onChangePhone = (val) => {
    const next = val.replace(/[^0-9+\-\s]/g, "");
    setPhoneNumber(next);
  };

  return (
    <div className="callSimulationPage" style={{ paddingTop: "70px" }}>
      <HStack m="0 10px 10px 0" className="sbscoHeadingRow">
        <Box w="100%">
          <Heading as="h4" size="md" noOfLines={1}>
            Call Simulation
          </Heading>
        </Box>
      </HStack>

      <HStack className="csWorkflowRow">
        <Box className="csWorkflowCard" bg="white" minHeight="150px">
          <Heading as="h6" size="xs" m="12px">
            Select workflow
          </Heading>
          <div className="csWorkflowSelect">
            <Select
              value={selectedWorkflow?.id || ""}
              onChange={(e) => onWorkflowChange(e.target.value)}
              w="100%"
              isDisabled={isInitiating || isSending}
            >
              {wfDetails.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
            {selectedWorkflow?.id ? (
              <Text className="csWorkflowHint">
                Selected: {selectedWorkflow.name}
              </Text>
            ) : null}
          </div>
        </Box>

        <Box className="csPhoneCard" bg="white">
          <Heading as="h6" size="xs" m="12px">
            Mobile number
          </Heading>
          <div className="csPhoneInputWrap" style={{ minHeight: "108px" }}>
            <Input
              type="tel"
              placeholder="Enter mobile number"
              value={phoneNumber}
              onChange={(e) => onChangePhone(e.target.value)}
              maxLength={20}
              isDisabled={isInitiating}
            />
            <Box className="csPhoneActions" display="flex" justifyContent="space-between" alignItems="center">
              <Button
                size="sm"
                bg="#9eceff"
                color="black"
                _hover={{
                  bg: "#7ebcff",
                }}
                _active={{
                  bg: "#6db3ff",
                }}
                borderRadius="md"
                fontWeight="semibold"
                px={6}
              >
                User Details
              </Button>
              <Button
                size="sm"
                onClick={initiateConversation}
                isLoading={isInitiating}
                isDisabled={
                  !phoneNumber || !selectedWorkflow?.id || isInitiating
                }
                bg="#9eceff"
                color="black"
                _hover={{
                  bg: "#7ebcff",
                }}
                _active={{
                  bg: "#6db3ff",
                }}
                borderRadius="md"
                fontWeight="semibold"
                px={6}
              >
                Initiate Conversation
              </Button>
            </Box>
            {phoneNumber ? (
              <Text className="csWorkflowHint">Selected: {phoneNumber}</Text>
            ) : null}
          </div>
        </Box>
      </HStack>

      {/* <Box className="chatContainer" bg="white">
        <div className="chatMessages" id="chatMessages">
          {messages.map((m) => (
            <div key={m.id} className={`chatRow ${m.role === "user" ? "alignRight" : "alignLeft"}`}>
              <div className={`chatBubble ${m.role}`}>
                <span className="chatText">{m.text}</span>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="chatInputRow">
          <Input
            className="chatInput"
            placeholder={conversationStarted ? "Type your message..." : "Initiate the conversation to start chatting"}
            value={pendingText}
            onChange={(e) => setPendingText(e.target.value)}
            onKeyDown={handleKeyDown}
            isDisabled={!conversationStarted || isSending}
          />
          <IconButton
            aria-label="Send"
            icon={<FiSend />}
            className="sendBtn"
            onClick={handleSend}
            isDisabled={!conversationStarted || isSending}
          />
        </div>
      </Box> */}
      <HStack align="start" spacing={6}>
        <Box
          className="chatContainer"
          bg="white"
          borderRadius="md"
          boxShadow="md"
          p={4}
          maxW="800px"
          height="500px"
          display="flex"
          flexDirection="column"
        >
          <div
            className="chatMessages"
            id="chatMessages"
            style={{
              flex: 1,
              overflowY: "auto",
              paddingRight: "8px",
              marginBottom: "1rem",
            }}
          >
            {messages.map((m) => (
              <div
                key={m.id}
                className={`chatRow ${
                  m.role === "user" ? "alignRight" : "alignLeft"
                }`}
              >
                <div
                  className={`chatBubble ${m.role}`}
                  style={{
                    backgroundColor: m.role === "user" ? "#DCF2FF" : "#F1F3F5",
                    borderRadius: "16px",
                    padding: "10px 14px",
                    maxWidth: "75%",
                    color: "#2D3748",
                    fontSize: "14px",
                    marginBottom: "8px",
                    boxShadow: "sm",
                  }}
                >
                  <span className="chatText">{m.text}</span>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div
            className="chatInputRow"
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              paddingTop: "8px",
              borderTop: "1px solid #EDF2F7",
            }}
          >
            <Input
              className="chatInput"
              placeholder={
                conversationStarted
                  ? "Type your message..."
                  : "Initiate the conversation to start chatting"
              }
              value={pendingText}
              onChange={(e) => setPendingText(e.target.value)}
              onKeyDown={handleKeyDown}
              isDisabled={!conversationStarted || isSending}
              backgroundColor="#F7FAFC"
              _placeholder={{ color: "#A0AEC0" }}
              _focus={{ borderColor: "#9eceff", boxShadow: "0 0 0 1px #9eceff" }}
              borderRadius="md"
            />
            <IconButton
              aria-label="Send"
              icon={<FiSend />}
              className="sendBtn"
              onClick={handleSend}
              isDisabled={!conversationStarted || isSending}
              bg="#9eceff"
              color="white"
              _hover={{ bg: "#7ebcff" }}
              _active={{ bg: "#6db3ff" }}
              borderRadius="md"
            />
          </div>
        </Box>

        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
          bg="gray.50"
          p={4}
          width="350px"
          height="440px"
          overflowY="auto"
        >
          <Text fontSize="lg" fontWeight="bold" mb={3}>
            Customer Details
          </Text>

          <VStack align="start" spacing={3}>
            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Death Cover (Original):</Text>
              <Text fontSize="sm" fontWeight="bold">197458</Text>
            </HStack>

            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Death Cover (Reduced):</Text>
              <Text fontSize="sm" fontWeight="bold">148094</Text>
            </HStack>

            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Due Amount:</Text>
              <Text fontSize="sm" fontWeight="bold">32987</Text>
            </HStack>

            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Get Value:</Text>
              <Text fontSize="sm" fontWeight="bold">293335</Text>
            </HStack>

            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Give Value:</Text>
              <Text fontSize="sm" fontWeight="bold">163600</Text>
            </HStack>

            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Insurance Co.:</Text>
              <Text fontSize="sm" fontWeight="bold">Bharti Axa</Text>
            </HStack>

            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Insured Name:</Text>
              <Text fontSize="sm" fontWeight="bold">Amarjeet</Text>
            </HStack>

            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Issue Date:</Text>
              <Text fontSize="sm" fontWeight="bold">26-Sep-2014</Text>
            </HStack>

            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Language:</Text>
              <Text fontSize="sm" fontWeight="bold">Hindi</Text>
            </HStack>

            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Last Due Date:</Text>
              <Text fontSize="sm" fontWeight="bold">20/05/2025</Text>
            </HStack>

            <HStack justify="space-between" w="100%">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">Late Fee:</Text>
              <Text fontSize="sm" fontWeight="bold">5305</Text>
            </HStack>
          </VStack>
        </Box>
      </HStack>
    </div>
  );
}
